<template>
  <div>
    <headline-container headline="Create Company" :breadcrumbs="breadcrumbs"></headline-container>

    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <h3>
            Details
          </h3>

          <p>
            Enter the details of the company, including the name, CVR and basic contact info.
          </p>
        </div>

        <div class="col-xs-18">
          <form @submit.prevent="submit">
            <div class="panel panel-default">
              <div class="panel-heading">
                <h5>
                  Basic Information
                </h5>
              </div>

              <div class="panel-body form-horizontal">
                <text-with-button-form-group
                    v-model="company.cvr"
                    name="cvr"
                    label="CVR"
                    help-text="The company's CVR number."
                    :error="getError('cvr')"
                    :button-text="lookupButtonText"
                    @click="lookupCVRNumber"
                ></text-with-button-form-group>

                <text-form-group
                    v-model="company.name"
                    name="name"
                    label="Name"
                    help-text="The name of the company."
                    :error="getError('name')"
                ></text-form-group>

                <text-form-group
                    v-model="company.ean"
                    name="ean"
                    label="EAN"
                    help-text="The company's EAN number."
                    :error="getError('ean')"
                ></text-form-group>

                <email-form-group
                    v-model="company.email"
                    name="email"
                    label="Invoice Email"
                    help-text="The company's invoice email."
                    :error="getError('email')"
                ></email-form-group>

                <text-with-button-form-group
                    v-model="company.phone"
                    name="phone"
                    label="Phone Number"
                    help-text="The company's phone number."
                    :error="getError('phone')"
                    :button-text="lookupButtonText"
                    @click="lookupCVRPhone"
                ></text-with-button-form-group>

                <text-form-group
                    v-model="company.website"
                    name="website"
                    label="Website"
                    help-text="The company's website."
                    :error="getError('website')"
                ></text-form-group>

                <static-form-group
                    label="Primary Industry"
                    :value="company.industry"
                    help-text="The company's primary industry."
                ></static-form-group>

                <static-form-group
                    label="Secondary Industries"
                    :value="company.secondary_industries"
                    help-text="The company's secondary industries."
                ></static-form-group>

                <text-form-group
                    v-model="company.number_of_employees"
                    name="number_of_employees"
                    label="Number Of Employees"
                    help-text="The number of employees in the company."
                    :error="getError('number_of_employees')"
                ></text-form-group>

                <date-form-group
                    v-model="company.established_at"
                    name="established_at"
                    label="Established At"
                    help-text="The date that the company was established."
                    :error="getError('established_at')"
                    format="DD.MM.YYYY"
                ></date-form-group>

                <tags-form-group
                    v-model="tags"
                    name="tags"
                    label="Tags"
                    help-text="Any number of tags that describe the company."
                    :error="getError('tags')"
                ></tags-form-group>

                <table-form-group
                    v-model="selectedPortals"
                    name="portals"
                    label="Portals"
                    help-text="The portals that the company is relevant for."
                    :error="getError('portals')"
                    :options="portalOptions"
                    option-label="Portal"
                ></table-form-group>
              </div>
            </div>

            <address-panel
                v-model="company.address"
                :countries="countries"
                :errors="errors"
            ></address-panel>

            <hr class="panel-separator">

            <button type="submit" class="btn btn-black" style="margin-bottom: 22px;">
              <i class="fa fa-save"></i> Save
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import AddressPanel from '@/components/AddressPanel'
import Errors from '@/mixins/Errors'
import DateFormGroup from '@/vendor/forms/FormGroups/Horizontal/Date'
import EmailFormGroup from '@/vendor/forms/FormGroups/Horizontal/Email'
import HeadlineContainer from '@/components/Layout/HeadlineContainer'
import Load from '@/mixins/Load'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import RadioFormGroup from '@/vendor/forms/FormGroups/Horizontal/Radio'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import StaticFormGroup from '@/vendor/forms/FormGroups/Horizontal/Static'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TableFormGroup from '@/components/FormGroups/Horizontal/Table'
import TagsFormGroup from '@/components/FormGroups/Horizontal/Tags'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import TextWithButtonFormGroup from '@/components/FormGroups/Horizontal/TextWithButton'
import TextareaFormGroup from '@/vendor/forms/FormGroups/Horizontal/Textarea'

export default {
  components: {
    AddressPanel,
    DateFormGroup,
    EmailFormGroup,
    HeadlineContainer,
    LoadingContainer,
    RadioFormGroup,
    SelectFormGroup,
    StaticFormGroup,
    SubmitFormGroup,
    TableFormGroup,
    TagsFormGroup,
    TextFormGroup,
    TextWithButtonFormGroup,
    TextareaFormGroup
  },

  mixins: [Errors, Load],

  data() {
    return {
      company: {
        name: null,
        address: {
          country: 'DK'
        }
      },
      tags: [],
      countries: [],
      portals: [],
      users: [],
      selectedPortals: []
    }
  },

  mounted() {
    this.load({
      countries: '/api/v2/countries',
      portals: '/api/v2/portals',
      users: '/api/v2/users'
    })
  },

  computed: {
    breadcrumbs() {
      return [
        {url: '/companies', text: 'Companies'},
        {text: 'Create'}
      ]
    },

    portalOptions() {
      return this.portals.data.map(portal => {
        return {
          value: portal.id,
          label: portal.attributes.name
        }
      })
    },

    userOptions() {
      return this.users.data.map(user => {
        return {
          value: user.id,
          label: user.attributes.full_name
        }
      })
    },

    lookupButtonText() {
      return '<i class="fa fa-search"></i> Lookup'
    }
  },

  methods: {
    submit() {
      http.post('/api/v1/companies', this.getRequestData())
          .then(response => {
            notify.success('The company was saved')

            this.$router.push('/companies/' + response.data.data.id)
          })
          .catch(this.catchValidationErrors)
    },

    getRequestData() {
      return {
        ...this.company,
        tags: this.tags.map(tag => tag.text),
        portals: this.selectedPortals.map(portal => {
          return {id: portal}
        })
      }
    },

    lookupCVRNumber() {
      http.get('/api/v2/cvr-companies/' + this.company.cvr)
          .then(response => this.saveCVRData(response.data.data, false))
    },

    lookupCVRPhone() {
      http.get('/api/v2/cvr-companies', {'filter[phone]': this.company.phone})
          .then(response => this.saveCVRData(response.data.data[0]))
    },

    saveCVRData(model, saveCVR = true) {
      if (!model) {
        return
      }

      if (saveCVR) {
        this.company.cvr = model.id
      }

      this.company.name = model.attributes.name

      this.company.email = model.attributes.email

      this.company.phone = model.attributes.phone

      this.company.industry = model.attributes.industry

      this.company.number_of_employees = model.attributes.number_of_employees

      this.company.established_at = model.attributes.established_at

      this.company.secondary_industries = model.attributes.secondary_industries.join('<br>')

      this.company.address = {...model.attributes.address, country: 'DK'}
    }
  }
}
</script>
